import {
  flow,
  getParent,
  getRoot,
  type Instance,
  types,
} from 'mobx-state-tree';
import type { IJobApplication } from './JobApplication';
import { STATE_ERROR, STATE_LOADED, stateType } from '../state';
import type { IStore } from '../Store';
import { logError } from '../../utils/logError';
import fetchCandidateById from '../../graphql/fetchCandidateById';
import { BUCKETS } from './types';
import { Rating } from './Rating';
import ProspectEmailStore from './ProspectEmailStore';
import Notes from './Notes';
import AttachmentsStore from './AttachmentsStore';
import type { Prospect as ProspectType } from '../../graphql/types';

export const PositionSummary = types.model('PositionSummary', {
  id: types.optional(types.string, ''),
  title: types.optional(types.string, ''),
  hasAccess: types.optional(types.boolean, true),
});

export const Prospect = types
  .model('Prospect', {
    id: types.identifier,
    candidateId: types.string,
    mostRecentJobTitle: types.optional(types.string, ''),
    mostRecentCompanyName: types.optional(types.string, ''),
    mostRecentRoleMonths: types.maybeNull(types.number),
    appliedDate: types.optional(types.string, ''),
    bucket: types.optional(
      types.enumeration<BUCKETS>('Buckets', Object.values(BUCKETS)),
      BUCKETS.UNKNOWN,
    ),
    attachmentsStore: types.optional(AttachmentsStore, {}),
    rating: types.optional(Rating, {}),
    source: types.optional(types.number, 0),
    prospectEmailStore: types.optional(ProspectEmailStore, {}),
    notes: types.optional(Notes, {}),
    positionSummary: types.optional(PositionSummary, {}),
  })
  .views((self) => ({
    get attachments() {
      return self.attachmentsStore.attachmentList;
    },
  }));

export interface IProspect extends Instance<typeof Prospect> {}

const DEFAULT_PROSPECT_COUNT = 1;

const CandidateStore = types
  .model('CandidateStore', {
    prospectCount: types.optional(types.number, DEFAULT_PROSPECT_COUNT),
    prospectList: types.array(Prospect),
    selectedPreviousApplicationId: types.maybeNull(types.string),
    state: stateType,
  })
  .views((self) => ({
    get rootStore() {
      return getRoot(self) as IStore;
    },

    get jobApplication(): IJobApplication {
      return getParent(self) as IJobApplication;
    },

    get previousApplicationCount() {
      return self.prospectCount - 1;
    },

    get previousApplications(): IProspect[] {
      return self.prospectList.slice(1);
    },
  }))
  .views((self) => ({
    get hasPreviousApplication() {
      return self.previousApplicationCount > 0;
    },

    get selectedPreviousApplication() {
      return self.previousApplications.find(
        (prospect) => prospect.id === self.selectedPreviousApplicationId,
      );
    },
  }))
  .actions((self) => {
    const selectPreviousApplication = (
      selectedPreviousApplicationId: string,
    ) => {
      self.selectedPreviousApplicationId = selectedPreviousApplicationId;
      if (
        self.selectedPreviousApplication &&
        self.selectedPreviousApplication.positionSummary.hasAccess
      ) {
        self.selectedPreviousApplication.prospectEmailStore.loadEmails();
        self.selectedPreviousApplication.notes.loadNotes();
      }
    };

    const loadCandidate: any = flow(function* (
      options: {
        autoSelectFirstOne?: boolean;
      } = {
        autoSelectFirstOne: true,
      },
    ) {
      if (self.state === STATE_LOADED) {
        return;
      }

      try {
        const candidate = yield fetchCandidateById({
          candidateId: (self.jobApplication as IJobApplication).candidateId,
          prospectId: (self.jobApplication as IJobApplication).id,
        });
        if (candidate) {
          self.prospectList = candidate.prospects.map(
            (prospect: ProspectType) => ({
              ...prospect,
              attachmentsStore: { attachmentList: prospect.attachments },
            }),
          );
          if (
            self.previousApplications.length > 0 &&
            options.autoSelectFirstOne
          ) {
            selectPreviousApplication(self.previousApplications[0].id);
          }

          self.state = STATE_LOADED;
        }
        return candidate.prospects;
      } catch (error: any) {
        self.state = STATE_ERROR;
        logError(
          {
            errorCode: 'LOAD_CANDIDATE',
            description: 'Error attempting: fetchCandidateById',
          },
          {
            error,
            candidateId: (self.jobApplication as IJobApplication).candidateId,
            prospectId: (self.jobApplication as IJobApplication).id,
          },
        );
      }
    });

    const unselectPreviousApplication = () => {
      self.selectedPreviousApplicationId = null;
    };

    return {
      loadCandidate,
      selectPreviousApplication,
      unselectPreviousApplication,
    };
  });

export interface ICandidateStore extends Instance<typeof CandidateStore> {}

export default CandidateStore;
